import '.a{display:flex;}.ssr-plugin'
import '.bd{flex:1;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.aq{justify-content:center;}.ssr-plugin'
import '.d5{min-height:0px;}.ssr-plugin'
import '.eu{padding-bottom:4.5rem;}.ssr-plugin'
import Row from '@app/components/layout/Row'
import { InitiativeCreatePageLayout } from '@app/pages/layout/InitiativeCreatePageLayout'
import { PhaseSelectionStep } from './steps/PhaseSelectionStep'
import { useCallback, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import { Button } from '@app/components/core/button/Button'
import useMediaQuery from '@app/util/useMediaQuery'
import { Phase, PhaseType, PHASE_TYPES } from '@schema/phase'
import {
  initiativeCreationSteps,
  InitiativeCreationStep,
  getCreationWorkflow,
  listedSteps,
} from '../shared/workflow'
import { useTranslations } from '@i18n/useLanguage'
import {
  faArrowLeft,
  faArrowRight,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons'
import { useAuthState, useClient, useQuery } from '@based/sdk/react'
import { Modal } from '@app/components/blocks/modal/Modal'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { Text } from '@app/components/core/typography/Text'
import { LoaderWrapper } from '@app/components/blocks/loader/LoaderWrapper'
import { FinishStep } from './steps/FinishStep'
import { useToast } from '@app/util/hooks/useToast'
import { styled } from 'inlines'
import { LivingVisionStep } from './steps/LivingVisionStep'
import { ImagesStep } from './steps/ImagesStep'
import { InitiativeDetailsStep } from './steps/InitiativeDetailsStep'
import { LivingAmenitiesStep } from './steps/LivingAmenitiesStep'
import { LivingTogetherStep } from './steps/LivingTogetherStep'
import { LocationStep } from './steps/LocationStep'
import { ContactStep } from './steps/ContactStep'
import type { InitiativeWithId } from '@app/pages/dashboard/initiative/shared/initiativeTypes'
import { CreationWorkflowOptionsProvider } from './blocks/CreationStepLayout'

const steps = initiativeCreationSteps

export const InitiativeCreationMenu = ({
  initiativeId,
  currentStep,
  initialPhase,
}: {
  initiativeId?: string
  currentStep?: string
  initialPhase?: PhaseType
}) => {
  const defaultStep = initialPhase ? steps.livingVision : steps.phaseSelection
  const stepValues = Object.values(steps)
  const step = Object.keys(steps).includes(currentStep ?? '')
    ? (currentStep as InitiativeCreationStep)
    : defaultStep
  const client = useClient()
  const authState = useAuthState()
  const [_, navigate] = useLocation()
  const translations = useTranslations().app.dashboard.initiative.creation
  const isMobile = useMediaQuery()
  const { showFailToast } = useToast()
  const phasesQuery: { loading: boolean; data?: Phase[] } =
    useQuery('list-phase')

  const [completedSteps, setCompletedSteps] = useState<
    Set<InitiativeCreationStep>
  >(
    new Set(
      step === steps.phaseSelection
        ? []
        : stepValues.slice(0, stepValues.indexOf(step)),
    ),
  )
  const [phase, setPhase] = useState<{ id: number; type: PhaseType } | null>(
    null,
  )
  const [newInitiativeId, setNewInitiativeId] = useState<string | undefined>(
    initiativeId,
  )
  const [initiative, setInitiative] = useState<InitiativeWithId | null>(null)
  const [exitConfirmationOpen, setExitConfirmationOpen] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)

  const setStep = useCallback(
    (nextStep: InitiativeCreationStep) => {
      navigate(
        pages.dashboardOnboardInitiative(newInitiativeId ?? '', nextStep),
      )
    },
    [newInitiativeId, navigate],
  )

  const currentWorkflow = useCallback(
    () =>
      getCreationWorkflow(phase?.type, {
        skipPhaseSelection: !!initialPhase,
      }),
    [phase, initialPhase],
  )

  const listedWorkflowSteps = phase ? listedSteps(currentWorkflow()) : []
  const progressStepIndex = listedWorkflowSteps.indexOf(step)
  const progress =
    listedWorkflowSteps.length > 0 && progressStepIndex >= 0
      ? {
          current: progressStepIndex + 1,
          total: listedWorkflowSteps.length,
        }
      : undefined

  const isNextStepEnabled = useCallback(() => {
    return completedSteps.has(step) || step === steps.finish
  }, [completedSteps, step])

  const isPreviousVisible = useCallback(() => {
    if (!phase || step === steps.finish) {
      return false
    }

    const workflow = currentWorkflow()
    const stepIndex = workflow.indexOf(step)

    if (stepIndex === -1) {
      throw new Error('Workflow error')
    }

    if (stepIndex === 0) {
      return false
    }

    return true
  }, [step, phase, currentWorkflow])

  const nextStepCallback = useCallback(() => {
    const workflow = currentWorkflow()
    const stepIndex = workflow.indexOf(step)

    if (stepIndex === -1) {
      throw new Error('Workflow error')
    }

    if (step === steps.finish) {
      navigate(pages.dashboardViewInitiative(newInitiativeId ?? ''))
      return
    }

    if (step === steps.contact && newInitiativeId) {
      setIsPublishing(true)
      client
        .call('publish-initiative', { id: Number(newInitiativeId) })
        .then((success) => {
          if (success) {
            setStep(steps.finish)
            return
          }
          showFailToast()
        })
        .catch(() => {
          showFailToast()
        })
        .finally(() => {
          setIsPublishing(false)
        })
      return
    }

    const nextStep = workflow[stepIndex + 1]
    if (!nextStep) {
      navigate(pages.dashboardViewInitiative(newInitiativeId ?? ''))
      return
    }
    setStep(nextStep)
  }, [
    step,
    newInitiativeId,
    setStep,
    currentWorkflow,
    navigate,
    client,
    showFailToast,
  ])

  const previousStepCallback = useCallback(() => {
    const workflow = currentWorkflow()
    const stepIndex = workflow.indexOf(step)

    if (stepIndex === -1) {
      throw new Error('Workflow error')
    }

    if (stepIndex === 0) {
      return
    }

    const previousStep = workflow[stepIndex - 1]
    setStep(previousStep)
  }, [step, setStep, currentWorkflow])

  const completeStep = useCallback(
    (completedStep: InitiativeCreationStep) => {
      setCompletedSteps((prev) =>
        prev.has(completedStep) ? prev : new Set(prev).add(completedStep),
      )
    },
    [setCompletedSteps],
  )
  const uncompleteStep = useCallback(
    (uncompletedStep: InitiativeCreationStep) => {
      setCompletedSteps((prev) => {
        const newSet = new Set(prev)
        newSet.delete(uncompletedStep)
        return newSet
      })
    },
    [setCompletedSteps],
  )

  useEffect(() => {
    if (!initialPhase || phase || !phasesQuery.data) {
      return
    }

    const initialPhaseRecord = phasesQuery.data.find(
      (phaseRecord) => phaseRecord.phaseType === initialPhase,
    )

    if (initialPhaseRecord) {
      setPhase({ id: initialPhaseRecord.id, type: initialPhase })
    }
  }, [initialPhase, phase, phasesQuery.data])

  useEffect(() => {
    if (!newInitiativeId) {
      return
    }

    return client
      .query('get-initiative', {
        id: Number(newInitiativeId),
        userId: authState.userId,
      })
      .subscribe((result) => {
        if (!result?.id) {
          setInitiative(null)
          return
        }
        setInitiative(result as InitiativeWithId)
        if (step !== steps.phaseSelection) {
          setPhase({ id: result.phase.id, type: result.phase.phaseType })
        }
      })
  }, [client, newInitiativeId, authState.userId, step])

  useEffect(() => {
    if (!phase) {
      return
    } else if (!newInitiativeId) {
      client
        .call('create-initiative', { phase: { id: phase.id } })
        .then((result) => {
          if (result) {
            setNewInitiativeId(result)
            completeStep(steps.phaseSelection)
          }
          if (!result) {
            showFailToast()
          }
        })
        .catch(() => {
          showFailToast()
        })
    } else if (step === steps.phaseSelection) {
      if (initiative?.phase?.id === phase.id) {
        completeStep(steps.phaseSelection)
        return
      }

      client
        .call('edit-initiative', {
          id: Number(newInitiativeId),
          value: { phase: { id: phase.id } },
        })
        .then((success) => {
          if (success) {
            completeStep(steps.phaseSelection)
            setInitiative((current) =>
              current
                ? {
                    ...current,
                    phase: { id: phase.id, phaseType: phase.type },
                  }
                : current,
            )
          } else {
            showFailToast()
          }
        })
        .catch(() => {
          showFailToast()
        })
    }
  }, [
    client,
    phase,
    step,
    newInitiativeId,
    initiative?.phase?.id,
    setNewInitiativeId,
    completeStep,
    showFailToast,
  ])

  const nextStepButton: { label: string; icon?: IconDefinition } = match(step)
    .with(steps.contact, () => ({
      label: translations.publishInitiative,
      icon: faArrowRight,
    }))
    .with(steps.finish, () => ({
      label: translations.done,
    }))
    .otherwise(() => ({
      label: translations.next,
      icon: faArrowRight,
    }))

  return (
    <CreationWorkflowOptionsProvider skipPhaseSelection={!!initialPhase}>
      <InitiativeCreatePageLayout
        title={translations.createInitiative}
        onClose={() => setExitConfirmationOpen(true)}
        progress={progress}
      >
        <div className="a bd b aq d5 eu"
        >
          {match(step)
            .with(steps.phaseSelection, () => (
              <PhaseSelectionStep
                selectedPhaseType={phase?.type}
                setSelectedPhase={setPhase}
              />
            ))
            .with(steps.livingVision, () => (
              <LoaderWrapper loading={!initiative}>
                <LivingVisionStep
                  initiative={initiative}
                  selectedPhaseType={phase?.type}
                  completeStep={completeStep}
                  uncompleteStep={uncompleteStep}
                />
              </LoaderWrapper>
            ))
            .with(steps.images, () => (
              <LoaderWrapper loading={!initiative}>
                <ImagesStep
                  initiative={initiative}
                  selectedPhaseType={phase?.type}
                  completeStep={completeStep}
                />
              </LoaderWrapper>
            ))
            .with(steps.initiativeDetails, () => (
              <LoaderWrapper loading={!initiative}>
                {phase?.type === PHASE_TYPES.living ? (
                  <LivingAmenitiesStep
                    initiative={initiative}
                    selectedPhaseType={phase.type}
                    completeStep={completeStep}
                    workflowStep={steps.initiativeDetails}
                  />
                ) : (
                  <InitiativeDetailsStep
                    initiative={initiative}
                    selectedPhaseType={phase?.type}
                    completeStep={completeStep}
                    uncompleteStep={uncompleteStep}
                  />
                )}
              </LoaderWrapper>
            ))
            .with(steps.livingTogether, () => (
              <LoaderWrapper loading={!initiative}>
                {phase?.type === PHASE_TYPES.living ? (
                  <InitiativeDetailsStep
                    initiative={initiative}
                    selectedPhaseType={phase?.type}
                    completeStep={completeStep}
                    uncompleteStep={uncompleteStep}
                    workflowStep={steps.livingTogether}
                  />
                ) : (
                  <LivingTogetherStep
                    initiative={initiative}
                    selectedPhaseType={phase?.type}
                    completeStep={completeStep}
                    uncompleteStep={uncompleteStep}
                  />
                )}
              </LoaderWrapper>
            ))
            .with(steps.livingAmenities, () => (
              <LoaderWrapper loading={!initiative}>
                <LivingTogetherStep
                  initiative={initiative}
                  selectedPhaseType={phase?.type}
                  completeStep={completeStep}
                  uncompleteStep={uncompleteStep}
                  workflowStep={steps.livingAmenities}
                />
              </LoaderWrapper>
            ))
            .with(steps.location, () => (
              <LoaderWrapper loading={!initiative}>
                {phase?.type === PHASE_TYPES.living ? (
                  <LivingTogetherStep
                    initiative={initiative}
                    selectedPhaseType={phase?.type}
                    completeStep={completeStep}
                    uncompleteStep={uncompleteStep}
                    workflowStep={steps.location}
                  />
                ) : (
                  <LocationStep
                    initiative={initiative}
                    selectedPhaseType={phase?.type}
                    completeStep={completeStep}
                    uncompleteStep={uncompleteStep}
                  />
                )}
              </LoaderWrapper>
            ))
            .with(steps.contact, () => (
              <LoaderWrapper loading={!initiative}>
                <ContactStep
                  initiative={initiative}
                  selectedPhaseType={phase?.type}
                  completeStep={completeStep}
                  uncompleteStep={uncompleteStep}
                />
              </LoaderWrapper>
            ))
            .with(steps.finish, () => <FinishStep />)
            .otherwise((): null => null)}
        </div>
        <Row
          alignItems="center"
          style={{
            justifyContent: 'space-between',
            flexDirection: 'row-reverse',
            position: 'fixed',
            width: isMobile ? '100%' : '85%',
            paddingInline: isMobile ? '5rem' : 0,
            maxWidth: '77.5rem',
            bottom: '1.5rem',
          }}
        >
          <Button
            label={nextStepButton.label}
            icon={nextStepButton.icon}
            disabled={!isNextStepEnabled() || isPublishing}
            onClick={nextStepCallback}
          />
          {isPreviousVisible() && (
            <Button
              label={translations.back}
              icon={faArrowLeft}
              iconPosition="left"
              variant="secondary"
              onClick={previousStepCallback}
            />
          )}
        </Row>
        <Modal
          title={translations.closeModalTitle}
          isOpen={exitConfirmationOpen}
          onClose={() => setExitConfirmationOpen(false)}
          onConfirm={() =>
            navigate(
              newInitiativeId
                ? pages.dashboardViewInitiative(newInitiativeId)
                : pages.dashboardInitiatives,
            )
          }
        >
          <Text.Medium style={{ whiteSpace: 'preserve', width: '20rem' }}>
            {translations.closeModalContent}
          </Text.Medium>
        </Modal>
      </InitiativeCreatePageLayout>
    </CreationWorkflowOptionsProvider>
  )
}
